﻿import React from 'react';
import { RowItem } from '../../../components/common';
import type { AddSubtitleHandler } from './types';

type SubtitleEditorListSourceItem = {
    id: number;
    text: string;
    timeRange: string;
};

type SubtitleEditorListTargetItem = {
    id: number;
    text: string;
    timeRange: string;
};

type SubtitleProcessingState = { isRetranslating: boolean; isRedubbing: boolean };

export type SubtitleEditorListProps = {
    sourcesText: SubtitleEditorListSourceItem[];
    targetText: SubtitleEditorListTargetItem[];
    videoData: any;

    roles: any[];
    rowSelectedRoles: Record<number, string>;

    editingRoleKey: string | null;
    editingRoleName: string;
    onEditingRoleNameChange: (name: string) => void;

    seriesSpeakers: any[];
    langCode: string;

    highlightedSubtitleId: number | null;
    isFromPure: boolean;
    isGlobalRetranslating: boolean;
    isGlobalRedubbing: boolean;
    subtitleProcessingStates: Record<number, SubtitleProcessingState>;
    updatingSubtitles: Record<number, boolean>;
    subtitleUpdateErrors: Record<number, string>;

    seriesVoiceLibraryMarkers: Record<number, boolean>;
    backTranslateData: any[];

    openHistorySubtitleId: number | null;

    // List callbacks (mostly passed through to RowItem)
    onSourceDataChange: any;
    onTargetDataChange: any;
    /** 点击目标台词时输出带 \\N 的批量更新请求体（调试用） */
    onTargetDialogueClick?: () => void;
    onRoleSelect: any;
    onNewSpeaker: any;
    onDeleteRole: any;
    onStartEditRole: any;
    onSaveRoleEdit: any;
    onCancelRoleEdit: any;
    onPromoteToSeriesSpeaker: any;
    onSeekToTime: any;
    volumeMap: Map<number, number>;
    onUpdateVolume: (subtitleId: number, volume: number) => void;
    speedMap: Map<number, number>;
    onUpdateSpeed: (subtitleId: number, speed: number) => void;

    onAudioPause: any;
    onAudioSync: any;

    onPlaySubtitle: any;
    onPlayTargetSubtitle: any;
    onUpdateVideoDataSpeed: any;

    onSwitchTrack: any;
    onDeleteSubtitle: any;
    onAddSubtitle: AddSubtitleHandler;
    onDeleteSeriesSpeaker: (speakerId: number) => void;

    onSubtitleUpdate: any;
    onGetNextSubtitle: any;
    onGetNextSubtitleId: any;
    onGetFullTimelineData: any;
    onRefreshSubtitles: any;

    onOpenHistoryPanel: any;
    onCloseHistoryPanel: any;

    // empty state
    isLoadingVideo: boolean;
}

const SubtitleEditorListImpl: React.FC<SubtitleEditorListProps> = (props) => {
    const {
        sourcesText,
        targetText,
        videoData,
        roles,
        rowSelectedRoles,
        editingRoleKey,
        editingRoleName,
        onEditingRoleNameChange,
        seriesSpeakers,
        langCode,
        highlightedSubtitleId,
        isFromPure,
        isGlobalRetranslating,
        isGlobalRedubbing,
        subtitleProcessingStates,
        updatingSubtitles,
        subtitleUpdateErrors,
        seriesVoiceLibraryMarkers,
        backTranslateData,
        openHistorySubtitleId,
        isLoadingVideo,
    } = props;

    if (!sourcesText || sourcesText.length === 0) {
        return (
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '200px',
                    color: '#666',
                    fontSize: '14px',
                }}
            >
                {isLoadingVideo ? '加载字幕数据中...' : '暂无字幕数据'}
            </div>
        );
    }

    return (
        <>
            {sourcesText.map((sourceItem, index) => {
                const fullSubtitleData = videoData?.subtitles?.find((sub: any) => sub.id === sourceItem.id);
                const targetItem =
                    targetText[index] || ({
                        id: sourceItem.id,
                        text: '',
                        timeRange: sourceItem.timeRange,
                        subtitle_id: fullSubtitleData?.subtitle_id || fullSubtitleData?.id,
                    } as any);

                return (
                    <RowItem
                        key={sourceItem.id}
                        sourceData={sourceItem}
                        targetData={targetItem}
                        commentText={(fullSubtitleData as any)?.notes || ''}
                        onSourceDataChange={props.onSourceDataChange}
                        onTargetDataChange={props.onTargetDataChange}
                        onTargetDialogueClick={props.onTargetDialogueClick}
                        roles={roles}
                        currentSpeakerId={fullSubtitleData?.speakers?.id}
                        selectedRole={
                            rowSelectedRoles[sourceItem.id] ||
                            videoData?.subtitles?.find((sub: any) => sub.id === sourceItem.id)?.speakers?.speaker_name ||
                            '未知说话人'
                        }
                        onRoleSelect={props.onRoleSelect}
                        onNewSpeaker={props.onNewSpeaker}
                        onDeleteRole={props.onDeleteRole}
                        showNewSpeaker={isFromPure}
                        subtitleData={fullSubtitleData as any}
                        onGetCurrentVolume={(subtitleId: number) =>
                            props.volumeMap.get(subtitleId) || fullSubtitleData?.volume_gain_db || 1.0
                        }
                        onUpdateVolume={props.onUpdateVolume}
                        onGetCurrentSpeed={(subtitleId: number) =>
                            props.speedMap.get(subtitleId) || fullSubtitleData?.audio_speed || 1.0
                        }
                        onUpdateSpeed={props.onUpdateSpeed}
                        editingRoleKey={editingRoleKey}
                        editingRoleName={editingRoleName}
                        onStartEditRole={props.onStartEditRole}
                        onSaveRoleEdit={props.onSaveRoleEdit}
                        onCancelRoleEdit={props.onCancelRoleEdit}
                        onEditingRoleNameChange={onEditingRoleNameChange}
                        seriesSpeakers={seriesSpeakers}
                        onPromoteToSeriesSpeaker={props.onPromoteToSeriesSpeaker}
                        onSeekToTime={props.onSeekToTime}
                        highlightedSubtitleId={highlightedSubtitleId}
                        hideTargetAndComment={isFromPure}
                        isPureMode={isFromPure}
                        isGlobalRetranslating={isGlobalRetranslating}
                        isGlobalRedubbing={isGlobalRedubbing}
                        subtitleProcessingState={subtitleProcessingStates[sourceItem.id] || { isRetranslating: false, isRedubbing: false }}
                        onAudioPause={props.onAudioPause}
                        onAudioSync={props.onAudioSync}
                        isSeriesSpeaker={seriesVoiceLibraryMarkers[sourceItem.id] || fullSubtitleData?.speakers?.is_series_speaker || false}
                        onPlaySubtitle={props.onPlaySubtitle}
                        onPlayTargetSubtitle={props.onPlayTargetSubtitle}
                        onUpdateVideoDataSpeed={props.onUpdateVideoDataSpeed}
                        isUpdating={updatingSubtitles[sourceItem.id] || false}
                        onSwitchTrack={props.onSwitchTrack}
                        updateError={subtitleUpdateErrors[sourceItem.id]}
                        seriesId={videoData?.series_id}
                        langCode={langCode}
                        onDeleteSubtitle={props.onDeleteSubtitle}
                        onAddSubtitle={props.onAddSubtitle}
                        onDeleteSeriesSpeaker={props.onDeleteSeriesSpeaker}
                        backTranslateText={
                            (fullSubtitleData as any)?.back_translate_text ||
                            backTranslateData.find((item: any) => item.id === sourceItem.id)?.backTranslateText ||
                            ''
                        }
                        onSubtitleUpdate={props.onSubtitleUpdate}
                        onGetNextSubtitle={props.onGetNextSubtitle}
                        onGetNextSubtitleId={props.onGetNextSubtitleId}
                        onGetFullTimelineData={props.onGetFullTimelineData}
                        onRefreshSubtitles={props.onRefreshSubtitles}
                        isHistoryPanelOpen={openHistorySubtitleId === sourceItem.id}
                        onOpenHistoryPanel={props.onOpenHistoryPanel}
                        onCloseHistoryPanel={props.onCloseHistoryPanel}
                        dubbingMethod={videoData?.dubbingMethod ?? null}
                    />
                );
            })}

            <div className="scroll-end-indicator">
                ——&nbsp;&nbsp;已展示全部内容&nbsp;&nbsp;——
            </div>
        </>
    );
};

export const SubtitleEditorList = SubtitleEditorListImpl;

